import api from 'services/api';
import { enforce, nullable, num, object, runSchema, str, unknownRecord, withDefault, type FieldParser } from 'validation';
import type { EventAction, EventConfig } from 'redux/slices/eventsSlice';

// `config` and `action` are per-event-type dynamic JSON: the server is the
// validation authority for their internals and the modal reads them defensively
// (optional chaining + per-field fallbacks). Validated only as "an object"
// (contents pass through verbatim), typed as the modal's shapes; a missing/null
// payload defaults to `{}` so the modal never dereferences null.
const opaque = <T>(): FieldParser<T> => withDefault(unknownRecord(), {}) as FieldParser<T>;

/**
 * A single event as returned by nap-backend `GET /events/:id` (routes/events.js
 * — `SELECT e.*, zone_name` + `folder_path`). This is the edit-relevant
 * projection re-derived from the backend source; the endpoint also returns
 * `zone_id`, `created_at`, `updated_at`, `audit_data` and `folder_path`, which
 * the modal does not consume, so the object parser drops them. `description` is
 * coerced to `string` (the wire column is nullable; the form binds it to an
 * input where `''` is the natural empty). Assignable to the slice `Event` shape
 * the edit modal accepts. Deeply readonly — validated wire data.
 */
export interface FetchedEvent {
  readonly event_id: number;
  readonly name: string;
  readonly description: string;
  readonly type: string;
  readonly zone_name: string;
  readonly folder_id: number | null;
  readonly config: EventConfig;
  readonly action: EventAction;
}

const eventSchema = object<FetchedEvent>({
  event_id: num(),
  name: str(),
  description: str(),
  type: str(),
  zone_name: str(),
  folder_id: nullable(num()),
  config: opaque<EventConfig>(),
  action: opaque<EventAction>(),
});

/**
 * Fetch a single event by id for the edit form when the list slice is empty
 * (e.g. launched from the Workspace). Boundary-validated (issue #757 step 3);
 * structural failure or a drifted identity (`event_id`) yields null so the
 * caller surfaces a "not found" instead of opening the modal on a degraded row.
 */
export const getEvent = async (id: number): Promise<FetchedEvent | null> => {
  try {
    const res = await api.get(`/events/${id}`);
    const result = runSchema(eventSchema, res.data, 'getEvent');
    if (!result.ok) return null;
    return enforce(result.value, result.warnings, ['event_id']);
  } catch {
    return null;
  }
};
